import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData } from "../util/getAuditTrailData";
import { AuditTrailForExport } from "../util/getAuditTrailData";

const baseUrl = BASE_URL + "canvas-page-template";

// 📌 Create Template (POST)
export const createCanvasPageTemplate = createAsyncThunk(
  "template/create",
  async ({ canvasdt, comment, version_content }, { rejectWithValue }) => {
    try {
      const ad = await getAuditTrailData({
        action: "CREATE_CANVAS_PAGE_TEMPLATE",
        comment: comment,
        version_content,
      });

      // Create the proper payload structure
      const payload = {
        canvas_page_template_name: canvasdt.canvas_page_template_name,
        canvas_page_category: canvasdt.canvas_page_category,
        canvas_page_template_description:
          canvasdt.canvas_page_template_description,
        layout_config: canvasdt.layout_config,
        location_markers: canvasdt.location_markers,
        text_box_properties: canvasdt.text_box_properties,
        text_box_content_data: canvasdt.text_box_content_data,
        shape_data: canvasdt.shape_data,
        image_data: canvasdt.image_data,
        canva_snap_url: canvasdt.canva_snap_url,
        audit_trail_data: ad,
      };

      const response = await axios.post(`${baseUrl}/create`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error creating page template"
      );
    }
  }
);
// 📌 Update Template (PUT)
export const updateCanvasPageTemplate = createAsyncThunk(
  "template/update",
  async ({ canvasdt, comment, version_content, id }, { rejectWithValue }) => {
    try {
      const ad = await getAuditTrailData({
        action: "UPDATE_CANVAS_PAGE_TEMPLATE",
        comment: comment,
        version_content,
      });

      // Create the proper payload structure
      const payload = {
        canvas_page_template_name: canvasdt.canvas_page_template_name,
        canvas_page_category: canvasdt.canvas_page_category,
        canvas_page_template_description:
          canvasdt.canvas_page_template_description,
        layout_config: canvasdt.layout_config,
        location_markers: canvasdt.location_markers,
        text_box_properties: canvasdt.text_box_properties,
        text_box_content_data: canvasdt.text_box_content_data,
        shape_data: canvasdt.shape_data,
        image_data: canvasdt.image_data,
        canva_snap_url: canvasdt.canva_snap_url,
        audit_trail_data: ad,
        remove_image_ids: canvasdt.remove_image_ids,
        remove_shape_ids: canvasdt.remove_shape_ids,
        remove_text_box_ids: canvasdt.remove_text_box_ids,
        remove_text_box_content_ids: canvasdt.remove_text_box_content_ids,
      };

      const response = await axios.put(
        `${baseUrl}/update/${id}/${canvasdt.pageId}/${canvasdt.textboxId}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error updating page template"
      );
    }
  }
);

// 📌 Get Template by ID (GET)
export const getCanvasPageTemplateById = createAsyncThunk(
  "template/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching canvas page template"
      );
    }
  }
);

// 📌 Get All Templates (GET)
export const getAllCanvasPageTemplates = createAsyncThunk(
  "template/getAll",
  async ({ limit = 100, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching canvas page templates"
      );
    }
  }
);

// 📌 Fetch All Templates with Search (GET)
export const fetchAllCanvasPageTemplates = createAsyncThunk(
  "template/fetchAll",
  async ({ searchQuery, limit, offset, searchType }, { rejectWithValue }) => {
    try {
      let url = `${baseUrl}/get-all`;
      const params = new URLSearchParams();

      if (searchQuery) {
        // If searchQuery already contains parameters, parse them
        if (searchQuery.includes("=")) {
          const searchParams = new URLSearchParams(searchQuery);
          for (let [key, value] of searchParams.entries()) {
            params.append(key, value);
          }
        } else {
          // If it's just a search term, add it as search parameter
          params.append("search", searchQuery);
        }
      }

      // Add search_type parameter if provided
      if (searchType) {
        params.append("search_type", searchType);
      }

      // Add pagination parameters if provided
      if (limit !== undefined && limit !== null) {
        params.append("limit", limit);
      }
      if (offset !== undefined && offset !== null) {
        params.append("offset", offset);
      }

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching canvas page templates"
      );
    }
  }
);
// 📌 Delete Template (DELETE)
export const deleteCanvasPageTemplate = createAsyncThunk(
  "template/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error deleting canvas page template"
      );
    }
  }
);

// 📌 Export CSV
export const exportCanvasTemplatesCSV = createAsyncThunk(
  "template/exportCSV",
  async (_, { rejectWithValue }) => {
    try {
      await AuditTrailForExport({
        apiUrl: `${baseUrl}/export-csv`,
        file_name: "canvas-template-data-" + Date.now() + ".csv",
        action: "EXPORT_CANVAS_TEMPLATE_DATA",
        action_to_show: "Canvas Template data exported as CSV",
      });
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error exporting canvas templates CSV"
      );
    }
  }
);

// 🛠 Slice
const canvasPageTemplateSlice = createSlice({
  name: "canvasPageTemplate",
  initialState: {
    templates: [],
    template: null,
    loading: false,
    error: null,
    pagination: {
      limit: 10, // default limit
      offset: 0, // default offset
      total: 0, // optional: total items count
    },
  },
  reducers: {
    setPagination: (state, action) => {
      const { limit, offset } = action.payload;
      if (limit !== undefined) state.pagination.limit = limit;
      if (offset !== undefined) state.pagination.offset = offset;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    setOffset: (state, action) => {
      state.pagination.offset = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCanvasPageTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCanvasPageTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates.push(action.payload);
      })
      .addCase(createCanvasPageTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = extractError(action.payload);
      })
      .addCase(updateCanvasPageTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCanvasPageTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = state.templates.map((template) =>
          template.id === action.payload.id ? action.payload : template
        );
      })
      .addCase(updateCanvasPageTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = extractError(action.payload);
      })
      .addCase(getCanvasPageTemplateById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCanvasPageTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        state.template = action.payload;
      })
      .addCase(getCanvasPageTemplateById.rejected, (state, action) => {
        state.loading = false;
        state.error = extractError(action.payload);
      })
      .addCase(getAllCanvasPageTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCanvasPageTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload.results;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllCanvasPageTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = extractError(action.payload);
      })
      .addCase(fetchAllCanvasPageTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCanvasPageTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload.results;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAllCanvasPageTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = extractError(action.payload);
      })
      .addCase(deleteCanvasPageTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCanvasPageTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = state.templates.filter(
          (template) => template.id !== action.payload
        );
      })
      .addCase(deleteCanvasPageTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = extractError(action.payload);
      })
      .addCase(exportCanvasTemplatesCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportCanvasTemplatesCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportCanvasTemplatesCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = extractError(action.payload);
      });
  },
});

const extractError = (payload) =>
  payload?.message || payload?.success || payload?.error || "An error occurred";

export const { setPagination, setLimit, setOffset } =
  canvasPageTemplateSlice.actions;
export default canvasPageTemplateSlice.reducer;
